'use client';

import { Fragment, useEffect, useState } from 'react';
import { Popover, Transition, Dialog } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

// Base navigation config; section entries get converted to hash links on the homepage
const navigationConfig = [
  { name: 'Features', section: 'features' },
  { name: 'Pricing', section: 'pricing' },
  { name: 'Guide', href: '/guide' },
  { name: 'GitHub', href: 'https://github.com/BleedingXiko/GhostHub', external: true },
  { name: 'Blog', href: '/blog' },
];

export default function Navbar() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  
  // Determine if we're on the homepage
  const isHomePage = pathname === '/';
  
  // Use appropriate navigation links based on current page
  const navigation = navigationConfig.map((item) => {
    if (item.section) {
      return {
        ...item,
        href: isHomePage ? `#${item.section}` : `/#${item.section}`,
        isAnchor: isHomePage,
      };
    }
    return item;
  });
      
  // Close mobile menu when route changes and detect client-side rendering
  useEffect(() => {
    setIsClient(true);
    setIsOpen(false);
  }, [pathname]);
  return (
    <Popover as="header" className="relative z-50">
      <div className="bg-dark-900 pt-6">
        <nav
          className="relative max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6"
          aria-label="Global"
        >
          <div className="flex items-center flex-1">
            <div className="flex items-center justify-between w-full md:w-auto">
              <Link href="/">
                <span className="sr-only">GhostHub</span>
                <div className="h-12 w-auto flex items-center">
                  <img 
                    className="h-10 w-auto" 
                    src="/icons/Ghosthub192.png" 
                    alt="GhostHub Logo"
                  />
                  <span className="ml-3 text-2xl font-bold text-white">GhostHub</span>
                </div>
              </Link>
              <div className="-mr-2 flex items-center md:hidden">
                <button
                  type="button"
                  className="bg-dark-800 rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => isClient && setIsOpen(true)}
                >
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="hidden space-x-8 md:flex md:ml-10">
              {navigation.map((item) => {
                const commonProps = {
                  key: item.name,
                  className: "text-base font-medium text-gray-300 hover:text-white",
                };

                if (item.external) {
                  return (
                    <a
                      {...commonProps}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.name}
                    </a>
                  );
                }

                if (item.isAnchor) {
                  return (
                    <a
                      {...commonProps}
                      href={item.href}
                    >
                      {item.name}
                    </a>
                  );
                }

                return (
                  <Link
                    {...commonProps}
                    href={item.href}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="hidden md:flex md:items-center md:space-x-6">
            <a
              href="/support"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-white bg-dark-800 hover:bg-dark-700"
            >
              Support
            </a>
            <a
              href="/checkout"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-ghost-600 hover:bg-ghost-700"
            >
              Order Now
            </a>
          </div>
        </nav>
      </div>

      {/* Mobile menu */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-40 md:hidden" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>
          
          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-col overflow-y-auto bg-dark-800 shadow-xl">
                <div className="flex items-center justify-between px-6 pt-6">
                  <div className="flex items-center">
                    <img 
                      className="h-10 w-auto" 
                      src="/icons/Ghosthub192.png" 
                      alt="GhostHub Logo"
                    />
                    <span className="ml-3 text-2xl font-bold text-white">GhostHub</span>
                  </div>
                  <button
                    type="button"
                    className="rounded-md p-2 text-gray-400 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ghost-500"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="space-y-6 py-6 px-6">
                  <nav className="space-y-1">
                    {navigation.map((item) => {
                      const commonProps = {
                        key: item.name,
                        className: "block px-3 py-3 rounded-md text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white",
                        onClick: () => setIsOpen(false),
                      };

                      if (item.external) {
                        return (
                          <a
                            {...commonProps}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.name}
                          </a>
                        );
                      }

                      if (item.isAnchor) {
                        return (
                          <a
                            {...commonProps}
                            href={item.href}
                          >
                            {item.name}
                          </a>
                        );
                      }

                      return (
                        <Link
                          {...commonProps}
                          href={item.href}
                        >
                          {item.name}
                        </Link>
                      );
                    })}
                  </nav>
                  <div>
                    <a
                      href="/checkout"
                      className="btn btn-primary w-full flex items-center justify-center py-3 text-base"
                      onClick={() => setIsOpen(false)}
                    >
                      Order Now
                    </a>
                    <a
                      href="/support"
                      className="mt-4 w-full flex items-center justify-center py-3 text-base font-medium text-gray-300 hover:text-white border border-gray-700 rounded-md hover:bg-gray-700"
                      onClick={() => setIsOpen(false)}
                    >
                      Support
                    </a>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </Popover>
  );
}
